/**
 * The response envelope — master spec §9.
 *
 * Responses are `{ data }` or `{ error: { code, message, details? } }`, never
 * a bare array. Codes are stable and machine-readable; the message is for a
 * human and may change.
 */

import { z } from 'zod';

/**
 * Every error code the API can return. Adding one here is the only way to
 * introduce one, which is what keeps them stable enough for the client to
 * branch on.
 */
export const ERROR_CODES = [
  // Auth and identity (§8d)
  'UNAUTHENTICATED',
  'TOKEN_INVALID',
  'TOKEN_EXPIRED',
  'APP_ONLY_TOKEN_REJECTED',
  'IDENTITY_CLAIMS_MISSING',
  'NO_ROLE_ASSIGNED',
  'ACCOUNT_DEACTIVATED',
  'FORBIDDEN',

  // Export control (§8) — the middleware lands in Phase 3, the codes are
  // declared here so both sides agree on them from the start.
  'EXPORT_BLOCKED',
  'EXPORT_POLICY_NOT_ACKNOWLEDGED',

  // Business rules (§6), Phase 3
  'STAGE_GATE_FAILED',
  'CLOSURE_REASON_INVALID',
  'DERIVED_FIELD_NOT_WRITABLE',
  'DELETE_HAS_CHILDREN',
  'APPEND_ONLY',
  'REASSIGNMENT_REQUIRED',

  // Attachments (§6.13), Phase 3
  'USE_MULTIPART',
  'CONTENT_TYPE_NOT_ALLOWED',
  'ATTACHMENT_TOO_LARGE',
  'UPLOAD_NOT_FOUND',

  // Generic
  'NOT_FOUND',
  'CONFLICT',
  'BAD_REQUEST',
  'VALIDATION_FAILED',
  'TIMEOUT',
  'INTERNAL_ERROR',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const errorCodeSchema = z.enum(ERROR_CODES);

export const apiErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.unknown().optional(),
    /** Server-generated UUIDv4, never taken from the request (§9). */
    requestId: z.string().uuid().optional(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

/** `{ data: T }`, for a schema T. */
export function dataEnvelope<T extends z.ZodTypeAny>(inner: T) {
  return z.object({ data: inner });
}

// ---------------------------------------------------------------------------
// Pagination (Phase 3, "Route surface")
// ---------------------------------------------------------------------------

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

/**
 * `?page`, `?pageSize`, `?sort`. Query strings arrive as strings, so both
 * numbers coerce — but `z.coerce.number()` accepts `''` as 0, which would make
 * an empty `?page=` silently mean page zero, so the empty string is rejected
 * first.
 */
export const paginationQuerySchema = z.object({
  page: z
    .string()
    .min(1)
    .regex(/^\d+$/)
    .transform(Number)
    .refine((n) => n >= 1, 'page starts at 1')
    .optional(),
  pageSize: z
    .string()
    .min(1)
    .regex(/^\d+$/)
    .transform(Number)
    .refine((n) => n >= 1 && n <= MAX_PAGE_SIZE, `pageSize is 1..${MAX_PAGE_SIZE}`)
    .optional(),
  /** `field` or `-field`. The resource decides which fields are sortable. */
  sort: z.string().min(1).max(64).optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/**
 * `{ data: { items, total, page, pageSize } }` — never a bare array (§9).
 *
 * `total` is honest: where a filter or sort forces evaluation of a derived
 * column the count runs over `v_opportunity_effective` too, rather than over
 * the base table with the filter quietly dropped (Phase 3, "Counting,
 * precisely").
 */
export function pageEnvelope<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.object({
      items: z.array(item),
      total: z.number().int().nonnegative(),
      page: z.number().int().positive(),
      pageSize: z.number().int().positive(),
    }),
  });
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * §6.10: concurrency is last-write-wins, surfaced as a non-blocking toast, not
 * as a 409. A mutation that overwrote somebody else's recent change returns
 * this beside the record so the client can say "Sarah also changed this 2
 * minutes ago". There is no `version` column and no conflict status.
 */
export const concurrencyNoticeSchema = z.object({
  changedBy: z.string(),
  changedByUserId: z.string().uuid(),
  changedAt: z.string(),
  fields: z.array(z.string()),
});

export type ConcurrencyNotice = z.infer<typeof concurrencyNoticeSchema>;
